import React, { useState } from 'react';
import { Play, Volume2, VolumeX, Settings } from 'lucide-react';
import { GoogleVoice, getAvailableVoices, getRecommendedVoicesForGeniusLoci } from '../services/googleVoiceService';

interface VoiceSelectorProps {
  selectedVoiceId?: string;
  onVoiceSelect: (voiceId: string) => void;
  onVoiceSettingsChange?: (settings: VoiceSettings) => void;
  className?: string;
}

export interface VoiceSettings {
  speed: number; // 0.25 - 4.0
  pitch: number; // -20.0 - 20.0
  volume: number; // 0.0 - 96.0
}

const VoiceSelector: React.FC<VoiceSelectorProps> = ({
  selectedVoiceId,
  onVoiceSelect,
  onVoiceSettingsChange,
  className = ''
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>({
    speed: 1.0,
    pitch: 0.0,
    volume: 0.0
  });

  const allVoices = getAvailableVoices();
  const recommendedVoices = getRecommendedVoicesForGeniusLoci();
  const selectedVoice = allVoices.find(v => v.id === selectedVoiceId);

  const handleVoiceSelect = (voiceId: string) => {
    onVoiceSelect(voiceId);
  };

  const handleSettingsChange = (newSettings: VoiceSettings) => {
    setVoiceSettings(newSettings);
    onVoiceSettingsChange?.(newSettings);
  };

  const playVoiceSample = async (voice: GoogleVoice) => {
    if (isPlaying) return;
    
    setIsPlaying(true);
    try {
      // Qui potresti implementare la riproduzione di un campione vocale
      // Per ora mostriamo solo un feedback visivo
      console.log(`🎵 Riproduzione campione per: ${voice.name} (${voice.id})`);
      
      // Simula la riproduzione
      setTimeout(() => {
        setIsPlaying(false);
      }, 2000);
    } catch (error) {
      console.error('Errore riproduzione campione:', error);
      setIsPlaying(false);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'neural': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'wave-net': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getGenderIcon = (gender: string) => {
    return gender === 'female' ? '👩' : gender === 'male' ? '👨' : '👤';
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Voce Selezionata */}
      {selectedVoice && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{getGenderIcon(selectedVoice.gender)}</span>
              <div>
                <h3 className="font-semibold text-primary">{selectedVoice.name}</h3>
                <p className="text-sm text-gray-600">{selectedVoice.description}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`px-2 py-1 text-xs rounded-full border ${getCategoryColor(selectedVoice.category)}`}>
                    {selectedVoice.category.toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-500">
                    {selectedVoice.sampleRate}Hz
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => playVoiceSample(selectedVoice)}
                disabled={isPlaying}
                className="p-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors duration-200 disabled:opacity-50"
                title="Ascolta campione"
              >
                {isPlaying ? <VolumeX size={16} /> : <Play size={16} />}
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors duration-200"
                title="Impostazioni voce"
              >
                <Settings size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Impostazioni Voce */}
      {showSettings && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <h4 className="font-medium text-gray-800 flex items-center space-x-2">
            <Settings size={16} />
            <span>Impostazioni Voce</span>
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Velocità */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Velocità: {voiceSettings.speed}x
              </label>
              <input
                type="range"
                min="0.25"
                max="4.0"
                step="0.25"
                value={voiceSettings.speed}
                onChange={(e) => handleSettingsChange({
                  ...voiceSettings,
                  speed: parseFloat(e.target.value)
                })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0.25x</span>
                <span>4.0x</span>
              </div>
            </div>

            {/* Tono */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tono: {voiceSettings.pitch > 0 ? '+' : ''}{voiceSettings.pitch}Hz
              </label>
              <input
                type="range"
                min="-20"
                max="20"
                step="1"
                value={voiceSettings.pitch}
                onChange={(e) => handleSettingsChange({
                  ...voiceSettings,
                  pitch: parseInt(e.target.value)
                })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>-20Hz</span>
                <span>+20Hz</span>
              </div>
            </div>

            {/* Volume */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Volume: {voiceSettings.volume}dB
              </label>
              <input
                type="range"
                min="0"
                max="96"
                step="1"
                value={voiceSettings.volume}
                onChange={(e) => handleSettingsChange({
                  ...voiceSettings,
                  volume: parseInt(e.target.value)
                })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0dB</span>
                <span>96dB</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Voci Consigliate */}
      <div>
        <h4 className="font-medium text-gray-800 mb-3 flex items-center space-x-2">
          <Volume2 size={16} />
          <span>Voci Consigliate per Genius Loci</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {recommendedVoices.map((voice) => (
            <button
              key={voice.id}
              onClick={() => handleVoiceSelect(voice.id)}
              className={`p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                selectedVoiceId === voice.id
                  ? 'border-primary bg-primary/10'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-3">
                <span className="text-xl">{getGenderIcon(voice.gender)}</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{voice.name}</div>
                  <div className="text-sm text-gray-600">{voice.description}</div>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full border ${getCategoryColor(voice.category)}`}>
                      {voice.category.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    playVoiceSample(voice);
                  }}
                  className="p-1 text-gray-400 hover:text-primary transition-colors duration-200 cursor-pointer disabled:opacity-50"
                >
                  <Play size={14} />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tutte le Voci */}
      <details className="group">
        <summary className="cursor-pointer font-medium text-gray-700 hover:text-primary transition-colors duration-200">
          Tutte le Voci Disponibili ({allVoices.length})
        </summary>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {allVoices.map((voice) => (
            <button
              key={voice.id}
              onClick={() => handleVoiceSelect(voice.id)}
              className={`p-3 rounded-lg border transition-all duration-200 text-left ${
                selectedVoiceId === voice.id
                  ? 'border-primary bg-primary/10'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">{getGenderIcon(voice.gender)}</span>
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-800">{voice.name}</div>
                  <div className="text-xs text-gray-500">{voice.id}</div>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full border mt-1 ${getCategoryColor(voice.category)}`}>
                    {voice.category}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </details>
    </div>
  );
};

export default VoiceSelector;
